import { blockCategories } from '../categories';

export default function (editor, opts = {}) {
    const blockManager = editor.BlockManager;
    const domComponents = editor.DomComponents;

    const languageOptions = [
        { value: 'de', name: 'Deutsch (de)' },
        { value: 'en', name: 'English (en)' },
        { value: 'fr', name: 'French (fr)' },
        { value: 'es', name: 'Spanish (es)' },
        { value: 'it', name: 'Italian (it)' },
        { value: 'pt', name: 'Portuguese (pt)' },
        { value: 'ru', name: 'Russian (ru)' },
        { value: 'zh', name: 'Chinese (zh)' },
        { value: 'ja', name: 'Japanese (ja)' },
        { value: 'ar', name: 'Arabic (ar)' },
        { value: 'hi', name: 'Hindi (hi)' },
        { value: 'nl', name: 'Dutch (nl)' },
        { value: 'pl', name: 'Polish (pl)' },
        { value: 'tr', name: 'Turkish (tr)' },
        { value: 'ko', name: 'Korean (ko)' },
    ];

    domComponents.addType('translated-text', {
        model: {
            defaults: {
                tagName: 'div',
                droppable: false,
                draggable: true,
                stylable: true,
                attributes: {
                    class: 'sio-translated-text',
                    'data-translations': '{"de":"Geben Sie hier Ihren Text ein"}',
                    'data-current-lang': 'de'
                },
                content: 'Geben Sie hier Ihren Text ein',
                traits: [],
            },

            init() {
                this.updateTraits();
                this.refreshDisplayText();
            },

            refreshDisplayText() {
                const translations = this.getTranslations();
                const currentLang = this.get('attributes')['data-current-lang'] || 'de';

                if (translations[currentLang]) {
                    const textContent = translations[currentLang];
                    this.components(textContent);
                    if (this.view && this.view.el) {
                        this.view.el.textContent = textContent;
                    }
                }
            },

            getTranslations() {
                const translationsAttr = this.get('attributes')['data-translations'] || '{}';
                try {
                    return JSON.parse(translationsAttr);
                } catch (e) {
                    return {};
                }
            },

            setTranslations(translations) {
                this.addAttributes({ 'data-translations': JSON.stringify(translations) });
            },

            updateTraits() {
                const self = this;
                const translations = this.getTranslations();
                const traits = [];

                const availableLangs = Object.keys(translations);
                const currentLang = this.get('attributes')['data-current-lang'] || 'de';

                traits.push({
                    type: 'select',
                    label: 'Display Language',
                    name: 'display-lang-selector',
                    value: currentLang,
                    options: availableLangs.length > 0
                        ? availableLangs.map(lang => {
                            const langOption = languageOptions.find(opt => opt.value === lang);
                            return {
                                value: lang,
                                name: langOption ? langOption.name : lang.toUpperCase()
                            };
                        })
                        : [{ value: 'de', name: 'Deutsch (de)' }],
                    changeProp: 1,
                });

                Object.entries(translations).forEach(([lang, text], index) => {
                    traits.push({
                        type: 'select',
                        label: `Language ${index + 1}`,
                        name: `lang-select-${index}`,
                        options: languageOptions,
                        value: lang,
                        changeProp: 1,
                    });

                    traits.push({
                        type: 'textarea',
                        label: `Translation ${index + 1}`,
                        name: `translation-text-${index}`,
                        value: text,
                        changeProp: 1,
                    });
                });

                traits.push({
                    type: 'select',
                    label: `New Language`,
                    name: `new-lang-selector`,
                    value: '',
                    options: [
                        { value: '', name: 'Select a language...' },
                        ...languageOptions
                    ],
                    changeProp: 1,
                });

                traits.push({
                    type: 'textarea',
                    label: `New Translation`,
                    name: `new-translation-text`,
                    value: '',
                    placeholder: 'Enter translation text',
                    changeProp: 1,
                });

                this.set('traits', traits);
            },

            updated(property, value, prev) {
                if (property === 'display-lang-selector') {
                    this.addAttributes({ 'data-current-lang': value });
                    this.refreshDisplayText();
                    return;
                }

                // Handle language code changes
                const langSelectMatch = property.match(/^lang-select-(\d+)$/);
                if (langSelectMatch) {
                    const index = parseInt(langSelectMatch[1]);
                    const translations = this.getTranslations();
                    const entries = Object.entries(translations);

                    if (entries[index]) {
                        const [oldLang, text] = entries[index];

                        if (value !== oldLang) {
                            // Create new translations object maintaining order
                            const newTranslations = {};
                            entries.forEach(([lang, txt], i) => {
                                if (i === index) {
                                    newTranslations[value] = txt;
                                } else {
                                    newTranslations[lang] = txt;
                                }
                            });

                            this.setTranslations(newTranslations);

                            const currentLang = this.get('attributes')['data-current-lang'];
                            if (currentLang === oldLang) {
                                this.addAttributes({ 'data-current-lang': value });
                            }

                            this.updateTraits();
                            this.refreshDisplayText();
                        }
                    }
                    return;
                }

                const translationTextMatch = property.match(/^translation-text-(\d+)$/);
                if (translationTextMatch) {
                    const index = parseInt(translationTextMatch[1]);
                    const translations = this.getTranslations();
                    const entries = Object.entries(translations);

                    if (entries[index]) {
                        const [lang] = entries[index];
                        translations[lang] = value;
                        this.setTranslations(translations);

                        const currentLang = this.get('attributes')['data-current-lang'];
                        if (currentLang === lang) {
                            this.refreshDisplayText();
                        }
                    }
                    return;
                }

                if (property === 'new-lang-selector' && value) {
                    const newText = this.get('new-translation-text') || '';

                    if (newText) {
                        const translations = this.getTranslations();
                        translations[value] = newText;
                        this.setTranslations(translations);

                        this.set('new-lang-selector', '');
                        this.set('new-translation-text', '');

                        this.updateTraits();
                    }
                    return;
                }

                if (property === 'new-translation-text' && value) {
                    const newLang = this.get('new-lang-selector') || '';

                    if (newLang) {
                        const translations = this.getTranslations();
                        translations[newLang] = value;
                        this.setTranslations(translations);

                        this.set('new-lang-selector', '');
                        this.set('new-translation-text', '');

                        this.updateTraits();
                    }
                    return;
                }
            }
        },
        view: {
            onRender() {
                const el = this.el;
                el.contentEditable = false;
                el.style.minHeight = '20px';
                el.style.cursor = 'pointer';

                const translations = this.model.getTranslations();
                const currentLang = this.model.get('attributes')['data-current-lang'] || 'de';
                if (translations[currentLang]) {
                    el.textContent = translations[currentLang];
                }
            }
        },
    });

    blockManager.add('translated-text', {
        label: 'Übersetzter Text',
        category: blockCategories.basic,
        media: `<svg viewBox="0 0 24 24">
            <path fill="currentColor" d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0 0 14.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8H4.69c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04M18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12m-2.62 7l1.62-4.33L19.12 17h-3.24Z"/>
        </svg>`,
        content: {
            type: 'translated-text',
        },
    });
}